import clsx from 'clsx'
import { iconNames } from 'lib/settings/icons'
import { useScreenMatch } from 'lib/utilities/screens'
import { ReactNode } from 'react'
import { Fragment } from 'react/jsx-runtime'
import Ampersand from '../../assets/images/ampersand.svg'
import CollapsibleSection from '../CollapsibleSection/CollapsibleSection'
import Icon from '../Icon/Icon'
import { ImageStandAlone as Image } from '../Image/Image'
import Link from '../Link/Link'
import Logo, { Brand } from '../Logo/Logo'

type IconKey = (typeof iconNames)[number]

interface Children {
  title: string | ReactNode
  url?: string
  onClick?: (e: React.MouseEvent) => void
  target?: string
}

interface PrimaryLinks {
  title: string
  children: Children[]
}

interface SecondaryLinks {
  title: string
  url: string
}

interface SocialLink {
  icon: IconKey
  url: string
}

export interface SiteFooterProps {
  /**
   * The brand to use.
   */
  brand: Brand
  /**
   * Flag to show the ampersand.
   */
  showAmpersand: boolean
  /**
   * Social links to display.
   */
  socialLinks: SocialLink[]
  /**
   * Primary links to display.
   */
  primaryLinks: PrimaryLinks[]
  /**
   * Secondary links to display.
   */
  secondaryLinks: SecondaryLinks[]
}

export default function SiteFooter({
  brand,
  showAmpersand,
  socialLinks,
  primaryLinks,
  secondaryLinks,
}: SiteFooterProps) {
  const isDesktop = useScreenMatch('lg')

  return (
    <footer
      className={clsx(
        'w-full bg-theme-primary text-white flex items-end gap-x-[122px] justify-center',
        'pb-14 lg:pb-0',
      )}
    >
      {showAmpersand && isDesktop && <Image src={Ampersand} alt="ampersand" />}
      <div
        className={clsx(
          'container md:mx-10 md:w-full py-8 md:pt-12 md:pb-20 lg:py-20 max-w-[960px]',
          showAmpersand && 'lg:pr-16',
        )}
      >
        <div className="col-span-full flex flex-col lg:flex-row justify-between items-center gap-x-12 gap-y-6 mb-12">
          <Logo brand={brand} width="120" height="66" />
          <ul className="flex justify-between items-center gap-6 [&_span]:align-middle">
            {socialLinks.map(({ icon, url }, key) => (
              <li key={key}>
                <Link
                  href={url}
                  className="border-0 align-middle"
                  underline={false}
                  inline
                  aria-label={`Link to ${icon}`}
                >
                  <Icon icon={icon} className="w-6 h-6 text-white" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div
          className={clsx(
            'col-span-full flex flex-col lg:flex-row justify-between items-start gap-x-[67px] gap-y-2 [&>*]:w-full lg:pb-6 lg:border-b border-neutral-400 mb-10 md:mb-12 lg:mb-10',
          )}
        >
          {primaryLinks.map(({ title, children }, primaryLinkKey) => (
            <Fragment key={primaryLinkKey}>
              {isDesktop ? (
                <div className="flex flex-col gap-4">
                  <h3 className="type-body-7">{title}</h3>
                  <ul className="flex flex-col gap-2">
                    {children.map(
                      ({ title, url, onClick, target }, childrenKey) => (
                        <li key={childrenKey}>
                          {url ? (
                            <Link
                              href={url}
                              className="[&]:text-white type-body-8 border-0"
                              inheritFont
                              underline={false}
                              inline
                              onClick={onClick}
                              target={target}
                            >
                              {title}
                            </Link>
                          ) : (
                            <div className="text-white type-body-8">
                              {title}
                            </div>
                          )}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              ) : (
                <CollapsibleSection
                  title={title}
                  overrideSectionClassName
                  sectionClassName="[&_button]:px-0 [&_button_span]:text-white [&_button+*]:px-0 [&_button]:font-semibold [&_button]:text-base border-b border-white "
                  panelClassName="sm-max:px-0"
                >
                  <ul className="flex flex-col gap-2">
                    {children.map(({ title, url, onClick }, childrenKey) => (
                      <li key={childrenKey}>
                        {url ? (
                          <Link
                            href={url}
                            target="_blank"
                            className="!text-white type-body-8 border-0"
                            underline={false}
                            inheritFont
                            inline
                            onClick={onClick}
                          >
                            {title}
                          </Link>
                        ) : (
                          <div className="text-white type-body-8">{title}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </CollapsibleSection>
              )}
            </Fragment>
          ))}
        </div>
        <div className="col-span-full flex md:justify-center lg:justify-start flex-col md:flex-row md:gap-x-6 lg:gap-x-12 gap-y-4">
          {secondaryLinks.map(({ title, url }, key) => (
            <div key={key}>
              <Link
                href={url}
                className="[&]:text-white text-xs border-0"
                underline={false}
                inheritFont
                inline
              >
                {title}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </footer>
  )
}
